import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Check, ArrowRight, Sparkles, Clock, BarChart3, 
  FileText, Crown, Headphones
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth, api } from '../context/AuthContext';
import { toast } from 'sonner';

const FlippexPlusPage = () => {
  const { user, setShowAuthModal } = useAuth();
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistStatus, setWaitlistStatus] = useState(null);
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);
  const [isAnnual, setIsAnnual] = useState(true);

  useEffect(() => {
    const fetchWaitlistStatus = async () => {
      if (user) {
        try {
          const response = await api.get('/onboarding/plus-waitlist/status');
          setWaitlistStatus(response.data);
        } catch (error) {
          console.error('Error fetching waitlist status:', error);
        }
      }
    };
    fetchWaitlistStatus();
  }, [user]);

  const handleJoinWaitlist = () => {
    if (!user) {
      setShowAuthModal(true);
    } else {
      setShowWaitlistModal(true);
    }
  };

  const confirmJoinWaitlist = async () => {
    try {
      setJoiningWaitlist(true);
      await api.post('/onboarding/plus-waitlist?source=plus_page_cta');
      toast.success("You're on the Flippex Plus waiting list!");
      setWaitlistStatus({ is_on_waitlist: true });
      setShowWaitlistModal(false);
    } catch (error) {
      console.error('Error joining waitlist:', error);
      toast.error('Failed to join waitlist. Please try again.');
    } finally {
      setJoiningWaitlist(false);
    }
  };

  // Base (Free) features
  const baseFeatures = [
    { text: "Browse all investment projects", included: true },
    { text: "Public investment window access", included: true },
    { text: "Basic project overview & photos", included: true },
    { text: "Portfolio holdings view", included: true },
    { text: "Email notifications", included: true },
    { text: "Standard support", included: true },
  ];

  // Plus features
  const plusFeatures = [
    { text: "Everything in Base", included: true, highlight: true },
    { text: "7-day private investment window", included: true },
    { text: "Investor pack: assumptions, risks, scenarios", included: true },
    { text: "Project scorecard for quick comparison", included: true },
    { text: "Enhanced dashboard: cashflows & distributions", included: true },
    { text: "Monthly portfolio statement (PDF + CSV)", included: true },
    { text: "Annual Tax Pack summary", included: true },
    { text: "Monthly investor call (Q&A + pipeline)", included: true },
    { text: "Priority support during funding", included: true },
  ];

  // Core features
  const coreFeatures = [
    {
      icon: Clock,
      title: "Early Access",
      description: "7-day private window before public launch"
    },
    {
      icon: BarChart3,
      title: "Deeper Insights",
      description: "Scorecards, scenarios & risk analysis"
    },
    {
      icon: FileText,
      title: "Premium Reporting",
      description: "Monthly statements & annual tax pack"
    },
    {
      icon: Headphones,
      title: "Priority Support",
      description: "Dedicated support during funding rounds"
    }
  ];

  // Simplified FAQ
  const faqItems = [
    {
      question: "Is Flippex Plus available now?",
      answer: "Not yet. Join the waiting list to get priority access when we launch."
    },
    {
      question: "Do I need Plus to invest?",
      answer: "No. All investors can participate in the public investment window. Plus gives you earlier access and better tools."
    },
    {
      question: "Does Plus guarantee allocation?",
      answer: "No. Plus provides earlier access, but allocations depend on availability and project terms."
    },
    {
      question: "What is the Annual Tax Pack?",
      answer: "A yearly summary of your investment activity and distributions. It's for your records, not tax advice."
    },
  ];

  const monthlyPrice = "€14.99";
  const annualPrice = "€149";
  const annualSavings = "Save €31";

  return (
    <>
      <Helmet>
        <title>Flippex Plus | Priority Access & Premium Tools</title>
        <meta name="description" content="Get priority access to investments, deeper insights, and premium reporting tools with Flippex Plus." />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <Header />

        <main className="flex-1">
          {/* HERO SECTION - matching About page style */}
          <section className="relative py-20 md:py-28 overflow-hidden">
            {/* Pink gradient - pink top-left → blue center → pink bottom-right */}
            <div className="absolute inset-0 z-0">
              <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, #D73986 0%, #6B2A5E 20%, #1a1f5e 50%, #6B2A5E 80%, #D73986 100%)', opacity: 0.35 }}></div>
              <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-pink-500/25 rounded-full blur-[100px] -translate-x-1/4 -translate-y-1/4"></div>
              <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-pink-500/25 rounded-full blur-[100px] translate-x-1/4 translate-y-1/4"></div>
            </div>
            
            <div className="container mx-auto px-6 md:px-12 lg:px-24 xl:px-32 relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                {/* Badge - matching About page style */}
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgb(var(--primary))]/10 border border-[rgb(var(--primary))]/20 mb-6">
                  <Sparkles className="w-4 h-4 text-[rgb(var(--primary))]" />
                  <span className="text-sm text-[rgb(var(--primary))] font-medium">Launching Soon</span>
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold tracking-tight mb-6">
                  Invest smarter with
                  <br />
                  <span className="text-gradient">Flippex Plus</span>
                </h1>

                <p className="text-lg md:text-xl text-[rgb(var(--foreground-muted))] mb-10 max-w-3xl mx-auto leading-relaxed">
                  Early access to deals, deeper insights, and premium reporting tools — all in one membership.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  {waitlistStatus?.is_on_waitlist ? (
                    <Button size="lg" className="rounded-full px-8 text-base" disabled>
                      <Check className="h-5 w-5 mr-2" />
                      You're on the waiting list
                    </Button>
                  ) : (
                    <Button 
                      size="lg" 
                      className="bg-primary hover:bg-primary-hover rounded-full px-8 text-base"
                      onClick={handleJoinWaitlist}
                    >
                      Join the waiting list <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* FEATURES & PRICING SECTION - Dark blue background */}
          <div className="relative bg-[rgb(var(--background))]">
            {/* CORE FEATURES SECTION */}
            <section className="py-16 relative z-10">
              <div className="container mx-auto px-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 max-w-5xl mx-auto">
                  {coreFeatures.map((feature, index) => (
                    <div key={index} className="flex flex-col items-center text-center p-4 md:p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-white/20 transition-all">
                      <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br from-pink-500/20 to-pink-600/10 flex items-center justify-center mb-3 md:mb-4">
                        <feature.icon className="h-6 w-6 md:h-7 md:w-7 text-pink-400" />
                      </div>
                      <h4 className="font-heading font-semibold text-sm md:text-lg mb-1 md:mb-2">{feature.title}</h4>
                      <p className="text-xs md:text-sm text-foreground-muted">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* PRICING CARDS */}
            <section className="py-12 md:py-16 relative z-10">
              <div className="container mx-auto px-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                  {/* BASE TIER */}
                  <Card className="relative p-8 bg-white/[0.03] border-white/10 hover:border-white/20 transition-all duration-300">
                    <div className="mb-6">
                      <h3 className="text-xl font-heading font-bold mb-2">Flippex Base</h3>
                      <p className="text-sm text-foreground-muted">For getting started</p>
                    </div>

                    <div className="mb-6">
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold">Free</span>
                      </div>
                      <p className="text-sm text-foreground-muted mt-1">Always free</p>
                    </div>

                    <Button 
                      variant="outline" 
                      className="w-full mb-8 rounded-full"
                      onClick={() => setShowAuthModal(true)}
                      disabled={!!user}
                    >
                      {user ? 'Current plan' : 'Get started'}
                    </Button>

                    <div className="space-y-3">
                      {baseFeatures.map((feature, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <Check className="h-5 w-5 text-foreground-muted flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-foreground-muted">{feature.text}</span>
                        </div>
                      ))}
                    </div>
                  </Card>

                  {/* PLUS TIER */}
                  <Card className="relative p-8 bg-gradient-to-b from-pink-500/15 via-pink-600/10 to-pink-900/10 border-pink-400/30 hover:border-pink-400/50 transition-all duration-300 shadow-xl shadow-pink-500/10">
                    {/* Popular badge */}
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-gradient-to-r from-pink-500 to-pink-600 text-white px-4 py-1 text-xs font-semibold border-0 shadow-lg shadow-pink-500/30">
                        <Crown className="h-3 w-3 mr-1" />
                        RECOMMENDED
                      </Badge>
                    </div>

                    <div className="mb-4 pt-2">
                      <h3 className="text-xl font-heading font-bold mb-2 flex items-center gap-2">
                        Flippex <span className="text-gradient">Plus</span>
                        <Sparkles className="h-4 w-4 text-pink-400" />
                      </h3>
                      <p className="text-sm text-foreground-muted">For serious investors</p>
                    </div>

                    {/* Billing Toggle - Inside Plus card on mobile */}
                    <div className="flex items-center justify-center gap-3 mb-4 p-3 rounded-lg bg-white/5">
                      <span className={`text-sm font-medium transition-colors ${!isAnnual ? 'text-foreground' : 'text-foreground-muted'}`}>
                        Monthly
                      </span>
                      <Switch 
                        checked={isAnnual} 
                        onCheckedChange={setIsAnnual}
                        className="data-[state=checked]:bg-pink-500"
                      />
                      <span className={`text-sm font-medium transition-colors ${isAnnual ? 'text-foreground' : 'text-foreground-muted'}`}>
                        Annual
                      </span>
                      {isAnnual && (
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                          {annualSavings}
                        </Badge>
                      )}
                    </div>

                    <div className="mb-6">
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-pink-400">
                          {isAnnual ? annualPrice : monthlyPrice}
                        </span>
                        <span className="text-foreground-muted">/{isAnnual ? 'year' : 'month'}</span>
                      </div>
                      <p className="text-sm text-foreground-muted mt-1">
                        {isAnnual ? `Billed annually (${monthlyPrice}/mo equivalent)` : 'Billed monthly'}
                      </p>
                    </div>

                    {waitlistStatus?.is_on_waitlist ? (
                      <Button className="w-full mb-8 rounded-full bg-white/10 hover:bg-white/20" disabled>
                        <Check className="h-4 w-4 mr-2" />
                        You're on the waiting list
                      </Button>
                    ) : (
                      <Button 
                        className="w-full mb-8 rounded-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white border-0 shadow-lg shadow-pink-500/25"
                        onClick={handleJoinWaitlist}
                      >
                        Join the waiting list
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    )}

                    <div className="space-y-3">
                      {plusFeatures.map((feature, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <Check className={`h-5 w-5 flex-shrink-0 mt-0.5 text-pink-400`} />
                          <span className={`text-sm ${feature.highlight ? 'text-pink-300 font-medium' : 'text-foreground'}`}>
                            {feature.text}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>

                {/* Trust text */}
                <p className="text-center text-xs text-foreground-muted mt-8">
                  No payment required to join the waiting list. Cancel anytime after launch.
                </p>
              </div>
            </section>

            {/* FAQ */}
            <section className="py-12 md:py-16 relative z-10">
              <div className="container mx-auto px-4">
                <div className="max-w-2xl mx-auto">
                  <h2 className="text-2xl font-heading font-bold text-center mb-8">
                    Questions?
                  </h2>

                  <Accordion type="single" collapsible className="space-y-2">
                    {faqItems.map((item, index) => (
                      <AccordionItem 
                        key={index} 
                        value={`item-${index}`} 
                        className="border border-white/10 rounded-lg px-4 bg-white/[0.02]"
                      >
                        <AccordionTrigger className="text-left text-sm hover:no-underline py-4">
                          {item.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-foreground-muted pb-4">
                          {item.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </div>
            </section>
          </div>

          {/* FINAL CTA - Same background as footer */}
          <section className="py-16 bg-background-paper">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4">
                Ready to invest smarter?
              </h2>
              <p className="text-foreground-muted mb-8 max-w-md mx-auto">
                Join the waiting list now. We'll notify you when Plus launches.
              </p>

              {waitlistStatus?.is_on_waitlist ? (
                <Button size="lg" className="rounded-full px-8" disabled>
                  <Check className="h-5 w-5 mr-2" />
                  You're on the waiting list
                </Button>
              ) : (
                <Button 
                  size="lg" 
                  className="rounded-full px-8 bg-primary hover:bg-primary-hover"
                  onClick={handleJoinWaitlist}
                >
                  Join the waiting list
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>

      {/* WAITLIST MODAL */}
      <Dialog open={showWaitlistModal} onOpenChange={setShowWaitlistModal}>
        <DialogContent className="sm:max-w-md bg-background/95 backdrop-blur-xl border-pink-500/20">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-pink-400" />
              Join the Plus waiting list
            </DialogTitle>
            <DialogDescription className="pt-2">
              We'll notify you when Flippex Plus launches. Waiting list members get priority access.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-0 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowWaitlistModal(false)}
              className="sm:mr-2 rounded-full"
            >
              Maybe later
            </Button>
            <Button 
              onClick={confirmJoinWaitlist}
              disabled={joiningWaitlist}
              className="rounded-full bg-gradient-to-r from-pink-500 to-pink-600 hover:from-pink-600 hover:to-pink-700 text-white border-0"
            >
              {joiningWaitlist ? 'Joining...' : 'Join waiting list'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FlippexPlusPage;
